import { useState } from 'react'
import { Linking, Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Info } from '@/components/ui/icons'
import { Image } from '@/components/ui/Image'
import { Pressable } from '@/components/ui/Pressable'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import type { TourGalleryImage } from '@/lib/types/tours.types'

const PHOTO_WIDTH = 224
const PHOTO_HEIGHT = 148

interface TourPhotoGalleryProps {
  photos: readonly TourGalleryImage[]
  onPhotoPress: (index: number) => void
}

export function TourPhotoGallery({ photos, onPhotoPress }: TourPhotoGalleryProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [creditsVisible, setCreditsVisible] = useState(false)

  if (photos.length === 0) return null

  const openExternal = async (url: string) => {
    try {
      await Linking.openURL(url)
    } catch {
      toast.error(t('tours.photo_gallery.link_error'))
    }
  }

  return (
    <View style={{ marginTop: SPACING.xl }} testID="tours.detail-gallery">
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flex: 1, paddingRight: SPACING.md }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
            {t('tours.photo_gallery.title')}
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textSecondary,
            }}
          >
            {t('tours.photo_gallery.hint')}
          </Text>
        </View>
        <View
          style={{
            minWidth: INTERACTION.minTouchTarget,
            height: INTERACTION.minTouchTarget,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: RADIUS.full,
            backgroundColor: colors.surfaceSubtle,
          }}
          accessibilityLabel={t('tours.photo_gallery.count', { count: photos.length })}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}>
            {photos.length}
          </Text>
        </View>
      </View>

      <ScrollView
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: SPACING.sm,
          paddingTop: SPACING.md,
          paddingRight: SPACING.lg,
        }}
        accessibilityLabel={t('tours.photo_gallery.scroll_label')}
        testID="tours.detail-gallery-scroll"
      >
        {photos.map((photo, index) => (
          <Pressable
            key={photo.id}
            onPress={() => onPhotoPress(index)}
            accessibilityRole="button"
            accessibilityLabel={t('tours.photo_gallery.open_photo', { label: photo.label })}
            testID={`tours.detail-gallery-photo-${photo.id}`}
            style={{ width: PHOTO_WIDTH }}
          >
            <View
              style={{
                width: PHOTO_WIDTH,
                height: PHOTO_HEIGHT,
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
                backgroundColor: colors.surfaceSubtle,
                borderWidth: 1,
                borderColor: colors.border,
              }}
            >
              <Image
                source={photo.imageUrl}
                blurhash={GENERIC_IMAGE_BLURHASH}
                width="100%"
                height="100%"
                borderRadius={RADIUS.lg}
                contentFit="cover"
                accessibilityLabel={photo.alt}
              />
              <View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  left: SPACING.sm,
                  right: SPACING.sm,
                  bottom: SPACING.sm,
                  alignSelf: 'flex-start',
                  backgroundColor: 'rgba(17, 15, 21, 0.74)',
                  borderRadius: RADIUS.full,
                  paddingHorizontal: SPACING.sm + 2,
                  paddingVertical: 4,
                }}
              >
                <Text
                  numberOfLines={1}
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: COLORS.white }}
                >
                  {photo.label}
                </Text>
              </View>
            </View>
          </Pressable>
        ))}
      </ScrollView>

      <Pressable
        onPress={() => setCreditsVisible((visible) => !visible)}
        accessibilityRole="button"
        accessibilityState={{ expanded: creditsVisible }}
        accessibilityLabel={t(
          creditsVisible ? 'tours.photo_gallery.hide_credits' : 'tours.photo_gallery.show_credits',
        )}
        testID="tours.detail-gallery-credits-toggle"
        style={{
          marginTop: SPACING.sm,
          minHeight: INTERACTION.minTouchTarget,
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'flex-start',
          gap: SPACING.xs,
        }}
      >
        <Info size={15} color={colors.textMuted} strokeWidth={1.8} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textSecondary }}>
          {t(
            creditsVisible
              ? 'tours.photo_gallery.hide_credits'
              : 'tours.photo_gallery.show_credits',
          )}
        </Text>
      </Pressable>

      {creditsVisible ? (
        <View
          style={{
            marginTop: SPACING.xs,
            borderRadius: RADIUS.lg,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.surfaceSubtle,
            padding: SPACING.md,
            gap: SPACING.md,
          }}
          testID="tours.detail-gallery-credits"
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 12,
              lineHeight: 18,
              color: colors.textSecondary,
            }}
          >
            {t('tours.photo_gallery.credits_intro')}
          </Text>
          {photos.map((photo) => (
            <View key={photo.id} style={{ gap: 3 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textPrimary }}>
                {photo.label}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  lineHeight: 17,
                  color: colors.textSecondary,
                }}
              >
                {t('tours.photo_gallery.credit_line', {
                  title: photo.attribution.title,
                  author: photo.attribution.author,
                })}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  lineHeight: 17,
                  color: colors.textMuted,
                }}
              >
                {photo.attribution.license} · {photo.attribution.adaptation}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.md }}>
                <Pressable
                  onPress={() => void openExternal(photo.attribution.sourceUrl)}
                  accessibilityRole="link"
                  accessibilityLabel={t('tours.photo_gallery.open_source', { label: photo.label })}
                  testID={`tours.detail-gallery-source-${photo.id}`}
                  style={{
                    minHeight: INTERACTION.minTouchTarget,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Text
                    style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textAccent }}
                  >
                    {t('tours.photo_gallery.source')}
                  </Text>
                  <ExternalLink size={12} color={colors.textAccent} strokeWidth={1.8} />
                </Pressable>
                <Pressable
                  onPress={() => void openExternal(photo.attribution.licenseUrl)}
                  accessibilityRole="link"
                  accessibilityLabel={t('tours.photo_gallery.open_license', { label: photo.label })}
                  testID={`tours.detail-gallery-license-${photo.id}`}
                  style={{
                    minHeight: INTERACTION.minTouchTarget,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Text
                    style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textAccent }}
                  >
                    {t('tours.photo_gallery.license')}
                  </Text>
                  <ExternalLink size={12} color={colors.textAccent} strokeWidth={1.8} />
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  )
}
